/**
 * v0.5 経営会議PDCA 増分「ブリーフ対話AI」P-5: 対話中のAI応答を施策として起票する軽量モーダル。
 * 設計: docs/design/v05-board-meeting-chat-detail.md §7 P-5。
 *
 * 既存 BoardInitiativesListPage.tsx の作成フォームと重複する部分はあるが、
 * 「施策にする」(BoardMeetingDetailPage.tsx の PromoteDraft、論点起点で KPI 目標欄を持つ) とも
 * 「施策を登録」(BoardInitiativesListPage.tsx、KPI 目標欄を持たない) とも別物であり、
 * 過度な共通化はしない (CLAUDE.md §6)。⚠️ 金額欄は持たせない (D-914)。
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { InitiativeOwnersEditor, type InitiativeOwner } from '@/components/board/InitiativeOwnersEditor';
import type { BoardNode } from '@/lib/board-node';

export function CreateInitiativeFromChatModal({
  open, onClose, originMeetingId, defaultTitle, defaultDescription, onCreated,
}: {
  open: boolean;
  onClose: () => void;
  originMeetingId: string;
  defaultTitle?: string;
  defaultDescription?: string;
  onCreated?: () => void;
}) {
  const [title, setTitle] = useState('');
  const [nodeId, setNodeId] = useState('');
  const [owners, setOwners] = useState<InitiativeOwner[]>([]);
  const [dueDate, setDueDate] = useState('');
  const [description, setDescription] = useState('');

  // ⚠️ P-5: node_id は必須 (InitiativeCreateSchema)。会議の scope_node_id を固定転用すると
  // own_store 参加者が 403 になるため、creatable-nodes の結果から選ばせる (単一なら自動選択)。
  const { data: creatableData } = useQuery<{ nodes: BoardNode[] }>({
    queryKey: ['board-initiatives', 'creatable-nodes'],
    queryFn: () => apiFetch('/board-initiatives/creatable-nodes'),
    enabled: open,
  });
  const creatableNodes = creatableData?.nodes ?? [];

  // open のたびに初期値を同期する (Modal は都度アンマウントされない共有インスタンスのため、
  // BoardMeetingDetailPage.tsx の EditMeetingModal と同じ簡易実装)
  useEffect(() => {
    if (!open) return;
    setTitle(defaultTitle ?? '');
    // AIの回答全文はDBの description 上限 (2000字、InitiativeCreateSchema) を超えうるため切り詰める
    setDescription((defaultDescription ?? '').slice(0, 2000));
    setOwners([]);
    setDueDate('');
    setNodeId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // creatable-nodes 読み込み完了時、選択肢が1件だけなら自動選択する (複数ある場合は明示選択させる)
  useEffect(() => {
    if (open && nodeId === '' && creatableNodes.length === 1) setNodeId(creatableNodes[0]!.node_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, creatableNodes.length]);

  const createMut = useMutation({
    mutationFn: () => apiFetch<{ initiative_id: string }>('/board-initiatives', {
      method: 'POST',
      body: JSON.stringify({
        node_id: nodeId,
        title: title.trim(),
        description: description.trim() || null,
        origin_meeting_id: originMeetingId,
        origin_finding_id: null,
        owners,
        due_date: dueDate || null,
      }),
    }),
    onSuccess: () => {
      toast.success('施策化しました');
      onCreated?.();
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '施策化に失敗しました'),
  });

  const canSave = title.trim() !== '' && nodeId !== '';

  return (
    <Modal open={open} title="施策化" onClose={onClose} size="md"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button variant="primary" disabled={!canSave || createMut.isPending} loading={createMut.isPending}
            onClick={() => createMut.mutate()}>
            施策化する
          </Button>
        </>
      )}>
      <div className="space-y-3">
        <div>
          <label className="block text-xs text-text-sub mb-1" htmlFor="cifc-title">タイトル</label>
          <input id="cifc-title" type="text" value={title} maxLength={200}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full min-h-[44px] px-3 rounded border border-surface-border text-sm" />
        </div>
        <div>
          <label className="block text-xs text-text-sub mb-1" htmlFor="cifc-node">対象組織</label>
          <Select value={nodeId} onValueChange={setNodeId}>
            <SelectTrigger id="cifc-node" aria-label="対象組織"><SelectValue placeholder="組織を選択" /></SelectTrigger>
            <SelectContent>
              {creatableNodes.map((n) => (
                <SelectItem key={n.node_id} value={n.node_id}>{'　'.repeat(n.depth_level)}{n.name} ({n.node_type_label})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-xs text-text-sub mb-1">担当者 (任意、複数可)</label>
          <InitiativeOwnersEditor value={owners} onChange={setOwners} enabled={open} />
        </div>
        <div>
          <label className="block text-xs text-text-sub mb-1" htmlFor="cifc-due">期限 (任意)</label>
          <input id="cifc-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
            className="w-full min-h-[44px] px-3 rounded border border-surface-border text-sm" />
        </div>
        <div>
          <label className="block text-xs text-text-sub mb-1" htmlFor="cifc-desc">説明 (AIの回答から引用、編集可)</label>
          <textarea id="cifc-desc" value={description} maxLength={2000} rows={6}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full px-3 py-2 rounded border border-surface-border text-sm" />
        </div>
      </div>
    </Modal>
  );
}
